/**
 * What: Reads the server-owned status words and tone for a saved test, and maps a status to the Tests-index filter
 *   taxonomy (its filter `key` and which `queue`/tab it shows under). The app owns NO status→label/tone map: `label` and
 *   `tone` always come from the Worker (`StudyAuthoringState` or a retained deletion receipt). `key`/`queue` is only the
 *   Tests list's own filter routing, not status presentation.
 */
import { shopifyComponentCopy } from '@testfeed/product-language'
import type { StudyAuthoringState } from '@testfeed/shopify-contracts'
import type {
  StudySetupMerchantQueue,
  StudySetupStatusKey,
  StudySetupStatusTone,
} from '../../components/study-setup-lifecycle'

type Presentation = {
  label: string
  tone: StudySetupStatusTone
  key: StudySetupStatusKey
  queue: StudySetupMerchantQueue
}

/** The Tests list's filter routing for a status: which filter key it carries and which queue/tab it belongs to. */
function queueOf(status: StudyAuthoringState['status']): {
  key: StudySetupStatusKey
  queue: StudySetupMerchantQueue
} {
  switch (status) {
    case 'deleting':
      return { key: 'deleting', queue: 'drafts' }
    case 'draft':
      return { key: 'draft', queue: 'drafts' }
    case 'preparing':
      return { key: 'preparing', queue: 'drafts' }
    case 'ready':
      return { key: 'prepared', queue: 'drafts' }
    case 'running':
      return { key: 'running', queue: 'running' }
    case 'complete':
      return { key: 'complete', queue: 'results-ready' }
    case 'failed':
      return { key: 'failed', queue: 'errors' }
    case 'blocked':
      return { key: 'failed', queue: 'errors' }
  }
}

/** A cleanup receipt is not another editable draft. Its words/tone are server-owned; only its queue routing is derived. */
export function studySummaryPresentation(
  summary: import('./studyBuilderDrafts').SavedStudyBuilderDraftSummary,
): Presentation {
  if (summary.state) return { label: summary.state.label, tone: summary.state.tone, ...queueOf(summary.state.status) }
  if (summary.deletion)
    return {
      label: summary.deletion.label,
      tone: summary.deletion.tone,
      key: summary.deletion.status === 'failed' ? 'failed' : 'deleting',
      queue: summary.deletion.status === 'failed' ? 'errors' : 'drafts',
    }
  if (summary.studyId) throw new Error('STUDY_AUTHORING_INVALID: Missing current study state')
  // A brand-new local draft that has never been saved: a single fixed default, not a status→presentation map.
  return { label: shopifyComponentCopy.studyAuthoringStatus.draft, tone: 'neutral', key: 'draft', queue: 'drafts' }
}
